// The phase terms that are not one of the five headline rules: the name, how
// the account carries over, the profitable-day floor, consistency and the day
// boundary. Collapsed, because most templates never touch them.

import { Show } from 'solid-js';

import { TIMEZONE_OPTIONS } from '../core/ruleRegistry';
import type { StepBuilderInput } from '../form';
import Dropdown from './Dropdown';
import NumberInput from './NumberInput';
import { useMoneyMark } from './builderWordsContext';

interface PhaseOptionsProps {
  step: StepBuilderInput;
  onUpdate: (partial: Partial<StepBuilderInput>) => void;
}

const CONSISTENCY_OFF: Partial<StepBuilderInput> = {
  consistencyMode: '',
  consistencyPct: '',
  consistencyBasis: 'total_net_profit',
  consistencyRemedy: 'fail_step',
  consistencyWindow: 'account_life',
};

export default function PhaseOptions(props: PhaseOptionsProps) {
  const mark = useMoneyMark();
  return (
    <details class="ch-card ch-disclose">
      <summary class="ch-disclose-head">More phase options</summary>
      <div class="ch-disclose-body ch-grid-3">
        <label class="ch-field">
          <span class="ch-label">Phase name</span>
          <input
            class="ch-input"
            value={props.step.displayName}
            placeholder="Evaluation"
            onInput={(event) => props.onUpdate({ displayName: event.currentTarget.value })}
          />
        </label>
        <div class="ch-field">
          {/* A fresh account starts at the stated size; carrying one over keeps
              the balance and the history the previous phase ended with. */}
          <span class="ch-label">Account for this phase</span>
          <Dropdown
            value={props.step.freshAccount ? 'fresh' : 'carry'}
            options={[
              { value: 'fresh', label: 'A fresh account' },
              { value: 'carry', label: 'The same account, carried over' },
            ]}
            onChange={(value) => props.onUpdate({ freshAccount: value === 'fresh' })}
          />
        </div>
        <div class="ch-field">
          <span class="ch-label">A trading day counts when a trade is</span>
          <Dropdown
            value={props.step.tradingDayMode}
            options={[
              { value: 'opened_trade', label: 'Opened' },
              { value: 'closed_trade', label: 'Closed' },
              { value: 'either', label: 'Opened or closed' },
            ]}
            onChange={(value) => props.onUpdate({ tradingDayMode: value })}
          />
        </div>
        <div class="ch-field">
          <span class="ch-label">Minimum profitable days</span>
          <NumberInput
            value={props.step.minProfitableDays}
            onInput={(value) => props.onUpdate({ minProfitableDays: value })}
            min={0}
            step={1}
            placeholder="none"
          />
        </div>
        <div class="ch-field">
          {/* What a day has to make to count as profitable. Blank counts any
              day that ends above where it opened. */}
          <span class="ch-label">A profitable day makes at least</span>
          <NumberInput
            value={props.step.minDailyProfit}
            onInput={(value) => props.onUpdate({ minDailyProfit: value })}
            prefix={mark}
            min={0}
            step={50}
            placeholder="any profit"
          />
        </div>
        <div class="ch-field">
          <span class="ch-label">Consistency</span>
          <Dropdown
            value={props.step.consistencyMode}
            options={[
              { value: '', label: 'No consistency rule' },
              { value: 'per_trade', label: 'Cap one trade' },
              { value: 'per_day', label: 'Cap one day' },
            ]}
            onChange={(value) => props.onUpdate(value ? { consistencyMode: value } : CONSISTENCY_OFF)}
          />
        </div>
        <Show when={props.step.consistencyMode}>
          <div class="ch-field">
            <span class="ch-label">Cap, as a share of total profit</span>
            <NumberInput
              value={props.step.consistencyPct}
              onInput={(value) => props.onUpdate({ consistencyPct: value })}
              min={1}
              max={100}
              step={5}
            />
          </div>
        </Show>
        <label class="ch-field">
          <span class="ch-label">The trading day resets at</span>
          <input
            class="ch-input"
            value={props.step.challengeDayBoundaryTime}
            placeholder="17:00"
            onInput={(event) => props.onUpdate({ challengeDayBoundaryTime: event.currentTarget.value })}
          />
        </label>
        <div class="ch-field">
          <span class="ch-label">In this timezone</span>
          <Dropdown
            value={props.step.challengeDayBoundaryTimezone}
            options={TIMEZONE_OPTIONS.map((zone) => ({ value: zone.value, label: zone.label }))}
            onChange={(value) => props.onUpdate({ challengeDayBoundaryTimezone: value })}
          />
        </div>
      </div>
    </details>
  );
}
